import * as stylex from '@stylexjs/stylex';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { PathComponents } from '@/utilities/PathComponents.atom';
import { faToggleOn } from '@seeqdev/icons/fa-pro-solid-svg-icons';
import { faToggleOff } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, Modal } from '@seeqdev/qomponents';

export interface AssetReplaceModalProps {
  onAssetSelection: (item: { id: string; name: string }) => void;
  onClose: () => void;
  selectedAsset: { id: string; name: string } | undefined;
  availableAssets: any[];
  likelySwapOuts: { id: string; name: string; disabled: boolean }[];
  size: 'sm' | 'lg';
  isSwappingScalingTableRow?: boolean;
}

export const AssetReplaceModal = ({
  onAssetSelection,
  onClose,
  selectedAsset,
  availableAssets,
  likelySwapOuts,
  size,
  isSwappingScalingTableRow = false,
}: AssetReplaceModalProps) => {
  const [showFullPaths, setShowFullPaths] = useState(
    !isSwappingScalingTableRow ? _.uniqBy(likelySwapOuts, 'name').length < likelySwapOuts.length : false,
  );
  const hasSelectedAsset = !!selectedAsset;

  const { t } = useTranslation();

  return (
    <Modal
      size={size}
      open={true}
      onClose={onClose}
      testId={isSwappingScalingTableRow ? 'scalingRowReplaceModal' : 'assetReplaceModal'}
      title={t(`${isSwappingScalingTableRow ? 'SCALING_ROW_REPLACE.TITLE' : 'ASSET_REPLACE.TITLE'}`)}
      hideSubmitButton={true}
      cancelButtonLabel={t('CANCEL')}>
      <div>
        <div {...stylex.props(styles.introduction)}>
          {t(`${isSwappingScalingTableRow ? 'SCALING_ROW_REPLACE.PROMPT' : 'ASSET_REPLACE.PROMPT'}`, {
            hasSelectedAsset,
            selectedAsset: selectedAsset?.name,
          })}
        </div>
        {!isSwappingScalingTableRow && (
          <>
            {t('SWAPS.SHOW_FULL_PATH')}
            <span {...stylex.props(styles.pathToggle)}>
              {showFullPaths ? (
                <a className="force-link-color" onClick={() => setShowFullPaths(false)}>
                  <Icon icon={faToggleOn} xstyle={styles.toggleIcon} large={true} />
                  {t('CONFIGURATION.ON')}
                </a>
              ) : (
                <a className="force-link-color" onClick={() => setShowFullPaths(true)}>
                  <Icon icon={faToggleOff} xstyle={styles.toggleIcon} large={true} />
                  {t('CONFIGURATION.OFF')}
                </a>
              )}
            </span>
          </>
        )}
        {showFullPaths
          ? _.map(availableAssets, (asset) => (
              <div {...stylex.props(styles.asset)} key={asset.id}>
                <PathComponents
                  asset={asset}
                  actionOnComponent={() => {
                    onClose();
                    onAssetSelection(asset);
                  }}
                />
              </div>
            ))
          : _.map(likelySwapOuts, (asset) => (
              <div {...stylex.props(styles.asset)} key={asset.id}>
                <FakeLink
                  xstyle={styles.assetLink}
                  onClick={() => {
                    onClose();
                    onAssetSelection(asset);
                  }}>
                  {asset.name}
                </FakeLink>
              </div>
            ))}
      </div>
    </Modal>
  );
};

const styles = stylex.create({
  introduction: { paddingBottom: '0.625rem' },
  pathToggle: { marginLeft: '0.3125rem', paddingLeft: '0.3125rem', paddingRight: '0.3125rem' },
  toggleIcon: { marginRight: 4 },
  asset: { paddingBottom: '0.625rem', paddingLeft: '0.625rem' },
  assetLink: { whiteSpace: 'nowrap' },
});
